import React from "react";
import { Alert, Button, Card, CardBody } from "reactstrap";

/**
 * ErrorBoundary - Componente para capturar errores en React
 * 
 * Envuelve componentes hijos y captura cualquier error de JavaScript
 * que ocurra en su árbol de componentes, mostrando una UI de fallback
 * en lugar de romper toda la aplicación.
 * 
 * Uso:
 * <ErrorBoundary>
 *   <ComponenteQuePodriaFallar />
 * </ErrorBoundary>
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error) {
    // Actualizar estado para mostrar UI de fallback
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Registrar error para debugging
    console.error("❌ Error capturado por ErrorBoundary:", error, errorInfo);
    
    this.setState({
      error: error,
      errorInfo: errorInfo,
    });

    // Aquí podrías enviar el error a un servicio de logging
    // como Sentry, LogRocket, etc.
    // logErrorToService(error, errorInfo);
  }

  handleReload = () => {
    window.location.reload();
  };

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  render() {
    if (this.state.hasError) {
      // UI de fallback personalizada
      return (
        <Card className="border-danger">
          <CardBody>
            <Alert color="danger" className="mb-3">
              <div className="d-flex align-items-center">
                <i className="ri-error-warning-line fs-20 me-2"></i>
                <div className="flex-grow-1">
                  <h5 className="alert-heading mb-2">
                    Ocurrió un error al cargar esta sección
                  </h5>
                  <p className="mb-0">
                    Lo sentimos, algo salió mal. Por favor, intenta recargar la página.
                  </p>
                </div>
              </div>
            </Alert>

            {/* Mostrar detalles del error solo en desarrollo */}
            {process.env.NODE_ENV === "development" && this.state.error && (
              <div className="mt-3">
                <details className="bg-light p-3 rounded">
                  <summary className="fw-bold text-danger cursor-pointer">
                    Detalles del error (solo visible en desarrollo)
                  </summary>
                  <div className="mt-2">
                    <p className="text-muted mb-2">
                      <strong>Error:</strong>
                    </p>
                    <pre className="bg-white p-2 rounded border text-danger small">
                      {this.state.error.toString()}
                    </pre>
                    
                    {this.state.errorInfo && (
                      <>
                        <p className="text-muted mb-2 mt-3">
                          <strong>Stack Trace:</strong>
                        </p>
                        <pre className="bg-white p-2 rounded border text-muted small" style={{ maxHeight: "200px", overflow: "auto" }}>
                          {this.state.errorInfo.componentStack}
                        </pre>
                      </>
                    )}
                  </div>
                </details>
              </div>
            )}

            <div className="d-flex gap-2 mt-3">
              <Button color="primary" onClick={this.handleReload}>
                <i className="ri-refresh-line me-1"></i>
                Recargar Página
              </Button>
              <Button color="secondary" outline onClick={this.handleReset}>
                <i className="ri-arrow-go-back-line me-1"></i>
                Intentar de Nuevo
              </Button>
            </div>
          </CardBody>
        </Card>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
